import type {
  BulkUpdateProgramsInput,
  BulkUpdateRequest,
  BulkUpdateResponse,
} from 'src/types/productLines/bulkEditProductHierarchy.types';
import {
  ProductHierarchyMinimalDetails,
  ProductHierarchyMinimalResponse,
  ProductLineDetail,
  ProductLineGroup,
  ProductLineGroupInput,
  ProductLineHierarchy,
  ProductLineListResponse,
  UpdateProductLineInput,
} from 'src/types/productLines/productHierarchy.types';
import {
  enrichProductHierarchyData,
  ProductLineWithMeta,
} from '../../components/manage-programs/utils/productHierarchyUtils';
import { bulkUpdatePrograms, createOrUpdateProductLineGroups, updateProductLine } from '../../graphql/mutations';
import {
  listMandatoryAttributes,
  listProductHierarchy,
  listProductHierarchyMinimal,
  listProductLineDetails,
  listProductLineGroup,
} from '../../graphql/queries';
import { formatApiError } from '../../utils/errorUtils';
import { logger } from '../../utils/logger';
import { fetchAllPaginatedData } from '../../utils/paginationUtils';
import { fetchGraphQLRequest } from './clients/graphqlClient';

/**
 * MandatoryAttribute type from GraphQL schema
 * Represents attributes that are required for specific product lines
 */
export interface MandatoryAttribute {
  attribute_type_label: string;
  product_code: string;
  product_line: string;
  product_line_id: number;
}

/**
 * Fetches all FPN (Finance Program Name) details from the GraphQL API with automatic pagination.
 * This function uses the generic pagination utility to handle multiple API calls until all pages are retrieved.
 *
 * The API returns paginated results with the following structure:
 * - data: Array of Product Line Hierarchy details (nested structure with RPNs and FPNs)
 * - pagination: Metadata including current_page, has_next, page_size, total_count, total_pages
 *
 * After fetching, this function enriches the data by adding hierarchy metadata fields (id, hierarchy_level, hierarchy_name)
 * to all nodes in the tree. These fields are required for Ag-Grid tree data and navigation functionality.
 *
 * @returns Promise resolving to array of all ProductLineWithMeta across all pages
 * @throws Error if any API call fails during pagination
 *
 * @example
 * ```typescript
 * try {
 *   const allFPNDetails = await fetchProductHierarchyAPI();
 *   console.log(`Fetched ${allFPNDetails.length} product line hierarchy records`);
 *   // Each node now has id, hierarchy_level, and hierarchy_name fields
 * } catch (error) {
 *   console.error('Failed to fetch Product Hierarchy details:', error);
 * }
 * ```
 */
export const fetchProductHierarchyAPI = async (): Promise<ProductLineWithMeta[]> => {
  try {
    const rawData = await fetchAllPaginatedData<ProductLineHierarchy, ProductLineListResponse>(
      async (page, pageSize) => {
        const result = await fetchGraphQLRequest<{ listProductHierarchy: ProductLineListResponse }>({
          query: listProductHierarchy,
          variables: {
            pagination: { page, page_size: pageSize },
          },
          requestName: 'listProductHierarchy',
          requestType: 'query',
        });
        return result.listProductHierarchy;
      },
      { logPrefix: 'Product Hierarchy details', pageSize: 5 }
    );

    // Enrich the data with hierarchy metadata fields (id, hierarchy_level, hierarchy_name)
    // These fields are derived client-side from the backend data and are required for:
    // - Ag-Grid tree data functionality (unique row IDs)
    // - Navigation (determining the correct route based on hierarchy level)
    // - Display (showing appropriate names in the hierarchy column)
    const enrichedData = enrichProductHierarchyData(rawData as ProductLineWithMeta[]);
    logger.info(`Enriched ${enrichedData.length} product line hierarchy records with metadata and display IDs`);

    return enrichedData;
  } catch (error) {
    logger.error('Error fetching Product Hierarchy details from GraphQL API:', { error });
    throw formatApiError('Failed to fetch Product Hierarchy details', error);
  }
};

/**
 * Fetches minimal Product Hierarchy data from the GraphQL API with automatic pagination.
 * This is a lightweight API that returns only essential fields for permission checks and navigation:
 * - Product Line: id, name, code
 * - RPN: id, alias, display_id, is_confidential
 * - FPN: id, alias, display_id
 *
 * Use this API when you only need basic hierarchy structure without detailed information.
 * For the full hierarchy with all fields, use fetchProductHierarchyAPI instead.
 *
 * @returns Promise resolving to array of all ProductHierarchyMinimalDetails across all pages
 * @throws Error if any API call fails during pagination
 *
 * @example
 * ```typescript
 * try {
 *   const minimalHierarchy = await fetchProductHierarchyMinimalAPI();
 *   console.log(`Fetched ${minimalHierarchy.length} minimal product line hierarchy records`);
 * } catch (error) {
 *   console.error('Failed to fetch minimal Product Hierarchy:', error);
 * }
 * ```
 */
export const fetchProductHierarchyMinimalAPI = async (): Promise<ProductHierarchyMinimalDetails[]> => {
  try {
    const allData = await fetchAllPaginatedData<ProductHierarchyMinimalDetails, ProductHierarchyMinimalResponse>(
      async (page, pageSize) => {
        const result = await fetchGraphQLRequest<{ listProductHierarchyMinimal: ProductHierarchyMinimalResponse }>({
          query: listProductHierarchyMinimal,
          variables: {
            pagination: { page, page_size: pageSize },
          },
          requestName: 'listProductHierarchyMinimal',
          requestType: 'query',
        });
        return result.listProductHierarchyMinimal;
      },
      { logPrefix: 'Minimal Product Hierarchy', pageSize: 100 }
    );

    logger.info(`Fetched ${allData.length} minimal product line hierarchy records`);
    return allData;
  } catch (error) {
    logger.error('Error fetching minimal Product Hierarchy from GraphQL API:', { error });
    throw formatApiError('Failed to fetch minimal Product Hierarchy', error);
  }
};

/**
 * Submits bulk updates for RPN and FPN fields.
 *
 * This function handles:
 * - Batching RPN updates
 * - Batching FPN updates
 * - Error aggregation across all updates
 *
 * @param request - The bulk update request containing RPN and FPN updates
 * @returns Promise resolving to the bulk update response
 * @throws Error if the API call fails
 *
 * @example
 * ```typescript
 * try {
 *   const response = await bulkUpdateProductHierarchyAPI({
 *     rpnUpdates: [{ product_line_id: 1, rpn_id: 100, updates: {...} }],
 *     fpnUpdates: [{ product_line_id: 1, rpn_id: 100, fpn_id: 200, updates: {...} }],
 *   });
 *   if (response.success) {
 *     console.log('Updated successfully');
 *   }
 * } catch (error) {
 *   console.error('Failed to update:', error);
 * }
 * ```
 */
/**
 * Response type from the bulkUpdatePrograms GraphQL mutation
 */
interface BulkUpdateProgramsResponse {
  bulkUpdatePrograms: {
    numberOfRecordsUpdated: number;
  };
}

export const bulkUpdateProductHierarchyAPI = async (request: BulkUpdateRequest): Promise<BulkUpdateResponse> => {
  try {
    logger.info('Starting bulk update', {
      rpn_updates: request.rpn_updates.length,
      fpn_updates: request.fpn_updates.length,
    });

    // Build the GraphQL input matching BulkUpdateProgramsInput schema
    const input: BulkUpdateProgramsInput = {
      rpn_updates: request.rpn_updates,
      fpn_updates: request.fpn_updates,
    };

    // Call the bulkUpdatePrograms GraphQL mutation
    const response = await fetchGraphQLRequest<BulkUpdateProgramsResponse>({
      query: bulkUpdatePrograms,
      variables: { input },
      requestName: 'bulkUpdatePrograms',
      requestType: 'mutation',
      auditConfig: { pageName: 'programs_bulk_edit', operationType: 'bulk_edit' },
    });

    const numberOfRecordsUpdated = response.bulkUpdatePrograms.numberOfRecordsUpdated;

    logger.info('Bulk update completed', {
      success: true,
      numberOfRecordsUpdated,
    });

    return {
      success: true,
      updatedRpnCount: request.rpn_updates.length,
      updatedFpnCount: request.fpn_updates.length,
      errors: [],
    };
  } catch (error) {
    logger.error('Bulk update failed with unexpected error', { error });
    throw formatApiError('Failed to complete bulk update', error);
  }
};

// RPN and FPN field updates now use existing APIs:
// - updateRpnAdditionalInfoAPI from productLinesService
// - updateFpnAdditionalInfoAPI from productLinesService
// These APIs accept the same mutation structure as individual edit forms.

/**
 * Fetches mandatory attributes configuration from the GraphQL API
 * Returns a list of attributes that are required for specific product lines
 *
 * This data is used for:
 * - Form validation to prevent saving when mandatory attributes are empty
 * - UI indication (red asterisk) to show which attributes are required
 * - Product line specific validation rules
 *
 * @returns Promise resolving to array of mandatory attributes
 * @throws Error if API call fails
 *
 * @example
 * ```typescript
 * try {
 *   const mandatoryAttrs = await fetchMandatoryAttributesAPI();
 *   console.log(`Fetched ${mandatoryAttrs.length} mandatory attribute rules`);
 * } catch (error) {
 *   console.error('Failed to fetch mandatory attributes:', error);
 * }
 * ```
 */
export const fetchMandatoryAttributesAPI = async (): Promise<MandatoryAttribute[]> => {
  try {
    logger.info('Fetching mandatory attributes configuration from API');

    const result = await fetchGraphQLRequest<{ listMandatoryAttributes: MandatoryAttribute[] }>({
      query: listMandatoryAttributes,
      variables: {},
      requestName: 'listMandatoryAttributes',
      requestType: 'query',
    });

    const mandatoryAttributes = result.listMandatoryAttributes || [];
    logger.info(`Fetched ${mandatoryAttributes.length} mandatory attribute rules from API`);

    return mandatoryAttributes;
  } catch (error) {
    logger.error('Error fetching mandatory attributes from GraphQL API:', { error });
    throw formatApiError('Failed to fetch mandatory attributes', error);
  }
};

/**
 * Updates a product line record with new values.
 *
 * @param input - The product line fields to update
 * @returns Promise resolving when update is complete
 * @throws Error if the API call fails
 *
 * @example
 * ```typescript
 * try {
 *   await updateProductLineAPI({
 *     product_line_id: 1,
 *     product_line: "Updated Product Line",
 *     product_line_group: "Group A",
 *     updated_by: "user@example.com"
 *   });
 *   console.log('Product line updated successfully');
 * } catch (error) {
 *   console.error('Failed to update product line:', error);
 * }
 * ```
 */
export const updateProductLineAPI = async (input: UpdateProductLineInput): Promise<void> => {
  try {
    logger.info('Updating product line', { product_line_id: input.product_line_id });

    await fetchGraphQLRequest<{ updateProductLine: { success: boolean; message: string } }>({
      query: updateProductLine,
      variables: { input },
      requestName: 'updateProductLine',
      requestType: 'mutation',
      auditConfig: { pageName: 'update_product_line' },
    });

    logger.info('Product line updated successfully', { product_line_id: input.product_line_id });
  } catch (error) {
    logger.error('Error updating product line:', { error, product_line_id: input.product_line_id });
    throw formatApiError('Failed to update product line', error);
  }
};

/**
 * Fetches all product line groups from the GraphQL API.
 * Product line groups are used for organizing and categorizing product lines.
 *
 * @returns Promise resolving to array of ProductLineGroup
 * @throws Error if API call fails
 *
 * @example
 * ```typescript
 * try {
 *   const groups = await fetchProductLineGroupsAPI();
 *   console.log(`Fetched ${groups.length} product line groups`);
 * } catch (error) {
 *   console.error('Failed to fetch product line groups:', error);
 * }
 * ```
 */
export const fetchProductLineGroupsAPI = async (): Promise<ProductLineGroup[]> => {
  try {
    logger.info('Fetching product line groups from API');

    const result = await fetchGraphQLRequest<{ listProductLineGroup: ProductLineGroup[] }>({
      query: listProductLineGroup,
      variables: {},
      requestName: 'listProductLineGroup',
      requestType: 'query',
    });

    const groups = result.listProductLineGroup || [];
    logger.info(`Fetched ${groups.length} product line groups from API`);

    return groups;
  } catch (error) {
    logger.error('Error fetching product line groups from GraphQL API:', { error });
    throw formatApiError('Failed to fetch product line groups', error);
  }
};

/**
 * Creates or updates product line groups.
 * Supports batch operations for multiple groups at once.
 *
 * @param input - Array of product line group inputs to create or update
 * @returns Promise resolving when operation is complete
 * @throws Error if the API call fails
 *
 * @example
 * ```typescript
 * try {
 *   await createOrUpdateProductLineGroupsAPI([
 *     {
 *       group_name: "Group A",
 *       description: "Description for Group A",
 *       item_metadata: {
 *         updated_by: "user@example.com",
 *         updated_at: new Date().toISOString()
 *       }
 *     }
 *   ]);
 *   console.log('Product line groups updated successfully');
 * } catch (error) {
 *   console.error('Failed to update product line groups:', error);
 * }
 * ```
 */
export const createOrUpdateProductLineGroupsAPI = async (input: ProductLineGroupInput[]): Promise<void> => {
  try {
    logger.info('Creating or updating product line groups', { count: input.length });

    await fetchGraphQLRequest<{ createOrUpdateProductLineGroups: { success: boolean; message: string } }>({
      query: createOrUpdateProductLineGroups,
      variables: { input },
      requestName: 'createOrUpdateProductLineGroups',
      requestType: 'mutation',
      auditConfig: { pageName: 'product_line_groups' },
    });

    logger.info('Product line groups updated successfully', { count: input.length });
  } catch (error) {
    logger.error('Error updating product line groups:', { error });
    throw formatApiError('Failed to update product line groups', error);
  }
};

/**
 * Fetches all product line details from the GraphQL API.
 * This returns a flat list of product lines without nested RPN/FPN hierarchy.
 *
 * Use this API when you need basic product line information without the full hierarchy structure.
 * For nested hierarchy with RPNs and FPNs, use fetchProductHierarchyAPI instead.
 *
 * @returns Promise resolving to array of all ProductLineDetail
 * @throws Error if API call fails
 *
 * @example
 * ```typescript
 * try {
 *   const productLines = await fetchProductLineDetailsAPI();
 *   console.log(`Fetched ${productLines.length} product lines`);
 * } catch (error) {
 *   console.error('Failed to fetch product line details:', error);
 * }
 * ```
 */
export const fetchProductLineDetailsAPI = async (): Promise<ProductLineDetail[]> => {
  try {
    logger.info('Fetching product line details from API');

    const result = await fetchGraphQLRequest<{ listProductLineDetails: ProductLineDetail[] }>({
      query: listProductLineDetails,
      variables: {},
      requestName: 'listProductLineDetails',
      requestType: 'query',
    });

    const productLineDetails = result.listProductLineDetails || [];
    logger.info(`Fetched ${productLineDetails.length} product line details from API`);

    return productLineDetails;
  } catch (error) {
    logger.error('Error fetching product line details from GraphQL API:', { error });
    throw formatApiError('Failed to fetch product line details', error);
  }
};

/**
 * Service for fetching and managing product hierarchy data
 */
export const productHierarchyService = {
  fetchProductHierarchyAPI,
  fetchProductHierarchyMinimalAPI,
  bulkUpdateProductHierarchyAPI,
  fetchMandatoryAttributesAPI,
  updateProductLineAPI,
  fetchProductLineGroupsAPI,
  createOrUpdateProductLineGroupsAPI,
  fetchProductLineDetailsAPI,
};
